// src/client/components/Hero.jsx
import React, { useEffect, useMemo, useState } from 'react';
import clientConfig from '../config/clientConfig';

const Hero = ({ theme, offer: offerProp }) => {
  const [slides, setSlides] = useState([]);

  const offer = offerProp || clientConfig.offer || 'Essentielle';

  const texts = clientConfig.texts?.hero || {};
  const navTexts = clientConfig.texts?.nav || {};
  const contactTexts = clientConfig.texts?.contact || {};

  const fallback = `/images/${theme}/hero.webp`;
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

  useEffect(() => {
    if (!theme) return;

    fetch(`${API_URL}/api/carousel?theme=${theme}`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setSlides(data);
        else setSlides([]);
      })
      .catch(() => setSlides([]));
  }, [theme, API_URL]);

  const heroImage = slides[0]?.image || fallback;

  const getAlt = () => texts.alt || navTexts.logoAlt || '';

  // ✅ CTA dynamiques (zéro texte en dur)
  const primaryCtaLabel = useMemo(() => {
    return (
      texts.ctaPrimaryLabel ||
      texts.button ||
      navTexts.ctaReservation ||
      navTexts.reservation ||
      ''
    );
  }, [texts, navTexts]);

  const secondaryCtaLabel = useMemo(() => {
    return texts.ctaSecondaryLabel || navTexts.ctaMenu || '';
  }, [texts, navTexts]);

  const primaryCtaHref = useMemo(() => {
    if (texts.ctaPrimaryHref) return texts.ctaPrimaryHref;
    if (offer === 'Réservation') return '#reservation';
    return '#menu';
  }, [texts, offer]);

  const secondaryCtaHref = useMemo(() => {
    return texts.ctaSecondaryHref || '#menu';
  }, [texts]);

  const ariaHome = texts.ariaHome || navTexts.ariaHome || '';

  // ✅ infos (pas de texte en dur)
  const city = navTexts?.city || navTexts?.locationUnknown || '';
  const address = contactTexts?.address || '';
  const phone = contactTexts?.phone || '';
  const hoursLabel = contactTexts?.hoursLabel || '';
  const hoursValue = contactTexts?.hours || '';

  // ✅ “badges” hero optionnels si le client veut (sinon vide)
  const heroBadges = Array.isArray(texts.badges) ? texts.badges : [];

  switch (theme) {
    case 'theme1': {
      const title = texts.welcome || texts.title || '';
      const subtitle = texts.subtitle || '';
      const micro = texts.micro || '';
      const eyebrow = texts.tagline || '';
      const showSecondary = !!secondaryCtaLabel;

      return (
        <section id="home" className="hero-image" aria-label={ariaHome}>
          <picture className="theme1-hero-picture">
            <source srcSet={heroImage} type="image/webp" />
            <img src={heroImage} alt={getAlt()} loading="eager" decoding="async" />
          </picture>

          <div className="theme1-hero-shade" aria-hidden="true" />

          <div className="hero-overlay">
            {eyebrow ? <p className="theme1-hero-eyebrow">{eyebrow}</p> : null}
            {title ? <h1 className="theme1-hero-title">{title}</h1> : null}
            {subtitle ? <p className="theme1-hero-subtitle">{subtitle}</p> : null}

            {(primaryCtaLabel || showSecondary) && (
              <div className="theme1-hero-ctas">
                {secondaryCtaLabel ? (
                  <a href={secondaryCtaHref} className="hero-btn">
                    {secondaryCtaLabel}
                  </a>
                ) : null}

                {primaryCtaLabel ? (
                  <a
                    href={primaryCtaHref}
                    className={`hero-btn ${secondaryCtaLabel ? 'hero-btn--ghost' : ''}`}
                  >
                    {primaryCtaLabel}
                  </a>
                ) : null}
              </div>
            )}

            {micro ? <p className="hero-micro">{micro}</p> : null}
          </div>
        </section>
      );
    }

    case 'theme2': {
      // ✅ structure différente : split layout + info panel
      const title = texts.welcome || texts.title || '';
      const subtitle = texts.subtitle || '';
      const eyebrow = texts.tagline || '';
      const micro = texts.micro || '';

      const showSecondary = !!secondaryCtaLabel;
      const showPrimary = !!primaryCtaLabel;

      const showInfoPanel = !!(address || phone || hoursValue || city || heroBadges.length);

      return (
        <section
          id="home"
          className="theme2-hero"
          style={{ backgroundImage: `url(${heroImage})` }}
          aria-label={ariaHome}
        >
          <div className="theme2-hero-overlay">
            <div className="theme2-hero-shell">
              {/* LEFT */}
              <div className="theme2-hero-left">
                {eyebrow ? <p className="theme2-hero-eyebrow">{eyebrow}</p> : null}
                {title ? <h1 className="theme2-hero-title">{title}</h1> : null}
                {subtitle ? <p className="theme2-hero-subtitle">{subtitle}</p> : null}

                {(showPrimary || showSecondary) && (
                  <div className="theme2-hero-ctas">
                    {showSecondary ? (
                      <a href={secondaryCtaHref} className="theme2-cta theme2-cta-ghost">
                        {secondaryCtaLabel}
                      </a>
                    ) : null}

                    {showPrimary ? (
                      <a href={primaryCtaHref} className="theme2-cta theme2-cta-primary">
                        {primaryCtaLabel}
                      </a>
                    ) : null}
                  </div>
                )}

                {micro ? <p className="theme2-hero-micro">{micro}</p> : null}
              </div>

              {/* RIGHT */}
              {showInfoPanel ? (
                <aside className="theme2-hero-right" aria-label={texts.infoAriaLabel || ''}>
                  {/* badges */}
                  {heroBadges.length > 0 ? (
                    <div className="theme2-hero-badges">
                      {heroBadges.slice(0, 6).map((b, i) => (
                        <span key={i} className="theme2-hero-badge">
                          {String(b || '')}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  <div className="theme2-hero-infos">
                    {city ? (
                      <div className="theme2-hero-info">
                        <span className="theme2-hero-info-k">{navTexts.locationLabel || ''}</span>
                        <span className="theme2-hero-info-v">{city}</span>
                      </div>
                    ) : null}

                    {address ? (
                      <div className="theme2-hero-info">
                        <span className="theme2-hero-info-k">{contactTexts.addressLabel || ''}</span>
                        <span className="theme2-hero-info-v">{address}</span>
                      </div>
                    ) : null}

                    {phone ? (
                      <div className="theme2-hero-info">
                        <span className="theme2-hero-info-k">{contactTexts.phoneLabel || ''}</span>
                        <a className="theme2-hero-info-v theme2-hero-link" href={`tel:${phone}`}>
                          {phone}
                        </a>
                      </div>
                    ) : null}

                    {hoursValue ? (
                      <div className="theme2-hero-info">
                        <span className="theme2-hero-info-k">{hoursLabel || ''}</span>
                        <span className="theme2-hero-info-v">{hoursValue}</span>
                      </div>
                    ) : null}
                  </div>

                  {/* micro note */}
                  {texts.note ? <div className="theme2-hero-note">{texts.note}</div> : null}
                </aside>
              ) : null}
            </div>
          </div>
        </section>
      );
    }

    case 'theme3':
      return (
        <section id="home" className="theme3-hero" aria-label={ariaHome}>
          <picture className="theme3-hero-picture">
            <source srcSet={heroImage} type="image/webp" />
            <img src={heroImage} alt={getAlt()} loading="eager" />
          </picture>
          <div className="theme3-hero-overlay">
            <h1 className="theme3-hero-title">{texts.welcome || texts.title || ''}</h1>
            <p className="theme3-hero-subtitle">{texts.subtitle || ''}</p>
          </div>
        </section>
      );

    case 'theme4':
      return (
        <section id="home" className="theme4-hero" aria-label={ariaHome}>
          <div className="theme4-hero-text">
            <h1 className="theme4-hero-title">{texts.welcome || texts.title || ''}</h1>
            <p className="theme4-hero-subtitle">{texts.subtitle || ''}</p>
          </div>
          <picture className="theme4-hero-image">
            <source srcSet={heroImage} type="image/webp" />
            <img src={heroImage} alt={getAlt()} loading="lazy" />
          </picture>
        </section>
      );

    case 'theme5':
      return (
        <section id="home" className="theme5-hero" aria-label={ariaHome}>
          <div className="theme5-hero-background">
            <picture>
              <source srcSet={heroImage} type="image/webp" />
              <img src={heroImage} alt={getAlt()} loading="lazy" />
            </picture>
          </div>
          <div className="theme5-hero-overlay">
            <div className="theme5-hero-text">
              <h1 className="theme5-hero-title">{texts.welcome || texts.title || ''}</h1>
              <p className="theme5-hero-subtitle">{texts.subtitle || ''}</p>
            </div>
          </div>
        </section>
      );

    case 'theme6': {
      const fallbackSlides = [
        { title: texts.welcome || texts.title || '', description: texts.subtitle || '', image: '/images/theme6/slide1.webp' },
        { title: texts.welcome || texts.title || '', description: texts.subtitle || '', image: '/images/theme6/slide2.webp' },
        { title: texts.welcome || texts.title || '', description: texts.subtitle || '', image: '/images/theme6/slide3.webp' },
      ];

      const slidesToRender = slides.length > 0 ? slides : fallbackSlides;

      const slideLabelPrefix = texts.slideLabelPrefix || '';
      const prevLabel = texts.prevLabel || '';
      const nextLabel = texts.nextLabel || '';

      return (
        <section id="home" className="theme6-hero" aria-label={ariaHome}>
          <div id="heroCarousel" className="carousel slide" data-bs-ride="carousel">
            <div className="carousel-indicators">
              {slidesToRender.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-bs-target="#heroCarousel"
                  data-bs-slide-to={i}
                  className={i === 0 ? 'active' : ''}
                  aria-label={slideLabelPrefix ? `${slideLabelPrefix} ${i + 1}` : ''}
                />
              ))}
            </div>

            <div className="carousel-inner">
              {slidesToRender.map((s, index) => (
                <div key={index} className={`carousel-item ${index === 0 ? 'active' : ''}`}>
                  <div className="row g-0 align-items-center theme6-slide">
                    <div className="col-md-6 p-5 theme6-slide-text">
                      <h1 className="theme6-slide-title">{s.title || ''}</h1>
                      <p className="theme6-slide-subtitle">{s.description || ''}</p>
                    </div>
                    <div className="col-md-6 theme6-slide-img">
                      <img
                        src={s.image}
                        className="d-block w-100"
                        alt={s.title || getAlt()}
                        loading={index === 0 ? 'eager' : 'lazy'}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button className="carousel-control-prev" type="button" data-bs-target="#heroCarousel" data-bs-slide="prev">
              <span className="carousel-control-prev-icon" aria-hidden="true" />
              <span className="visually-hidden">{prevLabel}</span>
            </button>

            <button className="carousel-control-next" type="button" data-bs-target="#heroCarousel" data-bs-slide="next">
              <span className="carousel-control-next-icon" aria-hidden="true" />
              <span className="visually-hidden">{nextLabel}</span>
            </button>
          </div>
        </section>
      );
    }

    default:
      return null;
  }
};

export default Hero;
